import { combineReducers } from 'redux';

import { AllActions } from '../actions/allActions';
import { InternalActionTypes } from '../constants/actionTypes';
import { NormalizedEvent } from '../models/normalized/normalizedEvent';
import { ById } from '../store/normalizedState';

import * as ReducerHelper from './reducerHelper';

function allIdsReducer(state: string[] = [], action: AllActions): string[] {
  switch (action.type) {
    case InternalActionTypes.EventLoaded:
      return action.result.entities.events[Object.keys(action.result.entities.events)[0]].archived
        ? state
        : ReducerHelper.addAllIds(state, action.result.entities.events);
    case InternalActionTypes.EventCreated:
      return ReducerHelper.addAllIds(state, action.result.entities.events);
    case InternalActionTypes.EventEdited:
    case InternalActionTypes.VideoReminderEmailSent:
    case InternalActionTypes.EventCancelled:
    case InternalActionTypes.UnlockTemplate:
      return ReducerHelper.addAllIds(state, action.result.entities.events);
    case InternalActionTypes.ActiveEventsLoaded:
      return action.result.entities.events ? Object.keys(action.result.entities.events) : [];
    case InternalActionTypes.EventAssigned:
      return ReducerHelper.addAllIds(state, action.result.entities.events);
    case InternalActionTypes.EventArchived:
      return ReducerHelper.removeId(state, action.result.result);
    case InternalActionTypes.EventUnarchived:
      return ReducerHelper.addAllIds(state, action.result.entities.events);
    default:
      return state;
  }
}

function byIdReducer(state: ById<NormalizedEvent> = {}, action: AllActions): ById<NormalizedEvent> {
  switch (action.type) {
    case InternalActionTypes.EventLoaded:
      return action.result.entities.events[Object.keys(action.result.entities.events)[0]].archived
        ? state
        : ReducerHelper.updateAll(state, action.result.entities.events);

    case InternalActionTypes.EventCreated:
      return ReducerHelper.updateAll(state, action.result.entities.events);
    case InternalActionTypes.EventEdited:
    case InternalActionTypes.VideoReminderEmailSent:
    case InternalActionTypes.EventCancelled:
    case InternalActionTypes.UnlockTemplate:
      return ReducerHelper.updateAll(state, action.result.entities.events);
    case InternalActionTypes.ActiveEventsLoaded:
      return ReducerHelper.updateAll(state, action.result.entities.events);
    case InternalActionTypes.EventAssigned:
      return ReducerHelper.updateAll(state, action.result.entities.events);
    case InternalActionTypes.EventArchived:
      return ReducerHelper.removeItem(state, action.result.result);
    case InternalActionTypes.EventUnarchived:
      return ReducerHelper.updateAll(state, action.result.entities.events);
    case InternalActionTypes.EventParticipantsLoaded:
      return ReducerHelper.updateItemByFunc(
        state,
        event => ({
          ...event,
          participants: ReducerHelper.mergeArrays(event.participants, action.result.result),
        }),
        action.eventId.toLowerCase(),
      );
    case InternalActionTypes.LiveAndHybridEventParticipantsLoadedAction:
      return ReducerHelper.updateItemByFunc(
        state,
        event => ({
          ...event,
        }),
        action.eventId.toLowerCase(),
      );
    case InternalActionTypes.EventParticipantAdded:
      return ReducerHelper.updateItemByFunc(
        state,
        event => ({
          ...event,
          participants: ReducerHelper.mergeArrays(event.participants, [action.result.result]),
        }),
        action.eventId.toLocaleLowerCase(),
      );
    case InternalActionTypes.EventParticipantDeleted:
      return ReducerHelper.updateItemByFunc(
        state,
        event => ({
          ...event,
          participants: ReducerHelper.removeId(event.participants, action.payload.participantId),
        }),
        action.payload.eventId.toLocaleLowerCase(),
      );
    case InternalActionTypes.EventParticipantEdited:
    case InternalActionTypes.ParticipantReviewStatusUpdated:
      return ReducerHelper.updateItemByFunc(
        state,
        event => ({
          ...event,
          participants: ReducerHelper.mergeArrays(event.participants, [action.result.result]),
        }),
        action.eventId.toLocaleLowerCase(),
      );

    case InternalActionTypes.EventParticipantsUploaded:
      return ReducerHelper.updateItemByFunc(
        state,
        event => ({
          ...event,
          participants: action.result.result,
        }),
        action.eventId.toLowerCase(),
      );
    case InternalActionTypes.MergedParticipants:
      return ReducerHelper.updateItemByFunc(
        state,
        event => ({
          ...event,
          participants: ReducerHelper.removeId(event.participants, action.removedParticipantId),
        }),
        action.eventId.toLowerCase(),
      );
    case InternalActionTypes.ConvertedToUnknownParticipant:
      return ReducerHelper.updateItemByFunc(
        state,
        event => ({
          ...event,
          participants: ReducerHelper.mergeArrays(event.participants, action.result.result),
        }),
        action.eventId.toLowerCase(),
      );
    case InternalActionTypes.EventTransitionLoaded:
      return ReducerHelper.updateItemByFunc(
        state,
        event => ({
          ...event,
          state: action.result.entities.eventTransitions[action.result.result].state,
        }),
        action.eventId.toLowerCase(),
      );
    case InternalActionTypes.EventVideosLoaded:
      return ReducerHelper.updateItemByFunc(
        state,
        event => ({
          ...event,
          eventVideos: action.result.result,
        }),
        action.eventId.toLowerCase(),
      );
    case InternalActionTypes.EventVideoLoaded:
      return ReducerHelper.updateItemByFunc(
        state,
        event => ({
          ...event,
          eventVideos: ReducerHelper.mergeArrays(event.eventVideos, [action.result.result]),
          state: action.result.entities.events[action.eventId].state,
        }),
        action.eventId.toLowerCase(),
      );
    case InternalActionTypes.EventStatisticsLoaded:
      // @ts-ignore
      return ReducerHelper.updateAll(state, action.result.entities.eventStatistics);
    case InternalActionTypes.EventActionsLoaded:
      return ReducerHelper.updateItemByFunc(
        state,
        event => ({
          ...event,
          eventActions: action.result.result,
        }),
        action.eventId.toLowerCase(),
      );
    case InternalActionTypes.UserDeleted:
      return Object.values(state).reduce((result, item) => {
        // eslint-disable-next-line no-param-reassign
        result[item.id] = { ...item, assignee: item.assignee === action.meta.userId ? undefined : item.assignee };

        return result;
      }, {});
    default:
      return state;
  }
}

export const eventsReducer = combineReducers({
  allIds: allIdsReducer,
  byId: byIdReducer,
});
